import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { Home, Calculator, Unlock, Lock, Loader2, CreditCard, CheckCircle, AlertCircle, XCircle, Download, X } from 'lucide-react';

// Beslutsläge components
import {
  ResultatBlock,
  InterpretationBlock,
  UncertaintyBlock,
  MissingBlock,
  DirectionBlock,
  BeslutslageTeaser,
  SaveBlock
} from '../beslutslage';
import BeslutslagePremium from '../beslutslage/BeslutslagePremium';
import CheckoutLoadingOverlay from '../beslutslage/CheckoutLoadingOverlay';

// PDF Generator
import { downloadBeslutsbrev, printBeslutsbrev } from '../../lib/beslutsbrevGenerator';

// Wartemodus
import { SALES_ENABLED } from '../../lib/featureFlags';

// Beslutsläge API
import { 
  createBeslutslageCheckout, 
  pollCheckoutStatus,
  getBeslutslageCase,
  checkBeslutslageReturn,
  clearBeslutslageParams,
  refreshCaseStatus
} from '../../lib/beslutslageApi';

/**
 * FastighetsvardeKalkylator - Property Value Calculator
 * 
 * Implements full Beslutsläge structure with Stripe payment:
 * "Skogskalkyl = von der Zahl zur Entscheidung"
 * 
 * COMMERCIAL FLOW:
 * 1. User calculates (FREE)
 * 2. User sees result blocks 1-5 (FREE)
 * 3. User clicks "Aktivera Beslutsläge – 149 kr"
 * 4. Stripe checkout → payment
 * 5. Redirect back → poll status
 * 6. Premium unlocked → blocks visible
 * 7. PDF downloadable
 */
const FastighetsvardeKalkylator = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const isLoggedIn = user !== null;
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Calculator state
  const [region, setRegion] = useState('svealand');
  const [areal, setAreal] = useState(50);
  const [virkesvolym, setVirkesvolym] = useState(7500);
  const [jaktvardeEnabled, setJaktvardeEnabled] = useState(false);
  const [jaktvarde, setJaktvarde] = useState(500);
  const [result, setResult] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  
  // Premium/Payment state
  const [isPremiumUnlocked, setIsPremiumUnlocked] = useState(false);
  const [isCheckingPayment, setIsCheckingPayment] = useState(false);
  const [isProcessingCheckout, setIsProcessingCheckout] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [paymentCancelled, setPaymentCancelled] = useState(false);
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const [currentCaseId, setCurrentCaseId] = useState(null);
  const [isLoadingCase, setIsLoadingCase] = useState(false);
  
  // Dev mode (for internal testing only)
  // Dev mode - ONLY localhost (security: prevents ?dev=true bypass in production)
  const [devMode] = useState(window.location.hostname === 'localhost' || 
                             window.location.hostname === '127.0.0.1');

  // Track calculator open on mount
  useEffect(() => {
  }, []); // Only on mount

  // Regional price factors
  const regionalFactors = {
    norrland: { pricePerM3sk: 450, landValueFactor: 0.15 },
    svealand: { pricePerM3sk: 580, landValueFactor: 0.20 },
    gotaland: { pricePerM3sk: 720, landValueFactor: 0.25 }
  };

  const content = {
    sv: {
      title: 'Fastighetsvärde indikator',
      description: 'Uppskatta värdet på en skogsfastighet baserat på areal, virkesvolym och region.',
      region: 'Region',
      regions: { norrland: 'Norra Sverige', svealand: 'Mellersta Sverige', gotaland: 'Södra Sverige' },
      areal: 'Areal (hektar)',
      virkesvolym: 'Virkesvolym (m³sk)',
      includeHunting: 'Inkludera jaktvärde',
      huntingValue: 'Jaktvärde (kr/ha)',
      calculate: 'Beräkna fastighetsvärde',
      defaults: 'Standardvärden baserade på regionala genomsnitt',
      activatePremium: 'Aktivera Beslutsläge – 149 kr',
      processing: 'Förbereder betalning...',
      checkingPayment: 'Vi kontrollerar betalningsstatus...',
      paymentSuccess: 'Betalning mottagen',
      paymentSuccessBanner: 'Tack för ditt köp',
      paymentSuccessDesc: 'Ditt beslutsbrev är nu tillgängligt. Vi har också skickat en bekräftelse till din e-postadress.',
      paymentSuccessContact: 'Om något inte fungerar, kontakta oss på info@hodema.se.',
      premiumActive: 'Beslutsläge aktiverat',
      premiumActiveDesc: 'Ditt beslutsbrev är tillgängligt',
      loginRequired: 'Logga in för att köpa Beslutsläge',
      devUnlock: 'Lås upp (Testläge)',
      devMode: 'Utvecklarläge',
      paymentCancelled: 'Betalningen avbröts',
      paymentCancelledDesc: 'Du kan fortsätta senare eller försöka igen',
      alreadyUnlocked: 'Det här ärendet är redan upplåst',
      loadingCase: 'Laddar ärende...',
      stripeSecure: 'Säker betalning via Stripe · Engångskostnad per ärende',
      tryAgain: 'Försök igen'
    },
    de: {
      title: 'Immobilienwert-Indikator',
      description: 'Schätzen Sie den Wert einer Waldimmobilie basierend auf Fläche, Holzvolumen und Region.',
      region: 'Region',
      regions: { norrland: 'Nordschweden', svealand: 'Mittelschweden', gotaland: 'Südschweden' },
      areal: 'Fläche (Hektar)',
      virkesvolym: 'Holzvolumen (m³)',
      includeHunting: 'Jagdwert einbeziehen',
      huntingValue: 'Jagdwert (SEK/ha)',
      calculate: 'Immobilienwert berechnen',
      defaults: 'Standardwerte basierend auf regionalen Durchschnitten',
      activatePremium: 'Entscheidungsgrundlage aktivieren – 149 SEK',
      processing: 'Zahlung wird vorbereitet...',
      checkingPayment: 'Zahlung wird überprüft...',
      paymentSuccess: 'Zahlung erfolgreich!',
      premiumActive: 'Premium aktiviert',
      loginRequired: 'Einloggen zum Kaufen',
      devUnlock: 'Freischalten (Testmodus)',
      devMode: 'Entwicklermodus'
    },
    en: {
      title: 'Property Value Indicator',
      description: 'Estimate forest property value based on area, timber volume and region.',
      region: 'Region',
      regions: { norrland: 'Northern Sweden', svealand: 'Central Sweden', gotaland: 'Southern Sweden' },
      areal: 'Area (hectares)',
      virkesvolym: 'Timber volume (m³)',
      includeHunting: 'Include hunting value',
      huntingValue: 'Hunting value (SEK/ha)',
      calculate: 'Calculate property value',
      defaults: 'Default values based on regional averages',
      activatePremium: 'Activate Decision State – 149 SEK',
      processing: 'Preparing payment...',
      checkingPayment: 'Checking payment...',
      paymentSuccess: 'Payment successful!',
      premiumActive: 'Premium active',
      loginRequired: 'Login to purchase',
      devUnlock: 'Unlock (Test mode)',
      devMode: 'Developer mode'
    },
    se: {
      title: 'Opmodatárvvu indikáhtor',
      description: 'Árvvoš meahcceopmodaga árvvu areála, muorravoluma ja guovllu vuođul.',
      region: 'Guovlu',
      regions: { norrland: 'Davvi Ruoŧŧa', svealand: 'Guovdu Ruoŧŧa', gotaland: 'Lulli Ruoŧŧa' },
      areal: 'Areála (hektárat)',
      virkesvolym: 'Muorravoluma (m³)',
      includeHunting: 'Lasit bivdoárvvu',
      huntingValue: 'Bivdoárvu (SEK/ha)',
      calculate: 'Rehkenas opmodatárvvu',
      defaults: 'Standárdaárvvut guovlluid gaskamearálaččat vuođul',
      activatePremium: 'Aktivere mearrádusláge – 149 kr',
      processing: 'Ráhkadeamen máksojurdaga...',
      checkingPayment: 'Dárkkistan máksojurdaga...',
      paymentSuccess: 'Máksu lihkostuvai!',
      premiumActive: 'Premium aktiiva',
      loginRequired: 'Čálit sisa oastit',
      devUnlock: 'Rahpat (Geahččalanvuohki)',
      devMode: 'Ovddidanvuohki'
    }
  };

  const c = content[language] || content.sv;

  // Check for return from Stripe on mount
  useEffect(() => {
    const checkReturn = async () => {
      const returnInfo = checkBeslutslageReturn();
      
      if (returnInfo.success && returnInfo.sessionId && returnInfo.caseId) {
        setIsCheckingPayment(true);
        setCurrentCaseId(returnInfo.caseId);
        setPaymentCancelled(false);
        // Clear URL params immediately so a refresh / navigation does not
        // re-trigger the polling loop and leave the UI stuck.
        clearBeslutslageParams();
        
        try {
          // Poll for payment status
          let attempts = 0;
          const maxAttempts = 10;
          
          const poll = async () => {
            const status = await pollCheckoutStatus(returnInfo.sessionId);
            
            if (status.payment_status === 'paid' && status.premium_unlocked) {
              // Payment successful - load the case
              const caseData = await getBeslutslageCase(returnInfo.caseId);
              
              // Restore calculation state
              if (caseData.calculation_data) {
                const { inputs, result: savedResult } = caseData.calculation_data;
                if (inputs) {
                  setRegion(inputs.region || 'svealand');
                  setAreal(inputs.areal || 50);
                  setVirkesvolym(inputs.virkesvolym || 7500);
                  setJaktvardeEnabled(inputs.jaktvardeEnabled || false);
                  setJaktvarde(inputs.jaktvarde || 500);
                }
                if (savedResult) {
                  setResult(savedResult);
                }
              }
              
              setIsPremiumUnlocked(true);
              setShowPaymentSuccess(true);
              setIsCheckingPayment(false);
              
              // Auto-hide success banner after 30 seconds
              setTimeout(() => setShowPaymentSuccess(false), 30000);
              return;
            }
            
            attempts++;
            if (attempts < maxAttempts) {
              setTimeout(poll, 2000);
            } else {
              setPaymentError(language === 'sv' ? 'Kunde inte bekräfta betalningen. Försök ladda om sidan.' : 'Payment verification timed out. Please refresh.');
              setIsCheckingPayment(false);
            }
          };
          
          await poll();
        } catch (error) {
          console.error('Payment check error:', error);
          setPaymentError(error.message);
          setIsCheckingPayment(false);
        }
      } else if (returnInfo.cancelled && returnInfo.caseId) {
        // User cancelled - show gentle message
        setPaymentCancelled(true);
        setCurrentCaseId(returnInfo.caseId);
        clearBeslutslageParams();
        
        // Try to load the case data to show results
        try {
          const caseData = await getBeslutslageCase(returnInfo.caseId);
          if (caseData.calculation_data) {
            const { inputs, result: savedResult } = caseData.calculation_data;
            if (inputs) {
              setRegion(inputs.region || 'svealand');
              setAreal(inputs.areal || 50);
              setVirkesvolym(inputs.virkesvolym || 7500);
              setJaktvardeEnabled(inputs.jaktvardeEnabled || false);
              setJaktvarde(inputs.jaktvarde || 500);
            }
            if (savedResult) {
              setResult(savedResult);
            }
          }
        } catch (e) {
          console.error('Could not load cancelled case:', e);
        }
      }
    };
    
    if (isLoggedIn) {
      checkReturn();
    }
  }, [isLoggedIn, language]);

  // Check for open_case URL parameter (from Mina ärenden)
  useEffect(() => {
    const openCaseId = searchParams.get('open_case');
    
    if (openCaseId && isLoggedIn && !isLoadingCase) {
      setIsLoadingCase(true);
      
      getBeslutslageCase(openCaseId)
        .then(caseData => {
          // Restore calculation state
          if (caseData.calculation_data) {
            const { inputs, result: savedResult } = caseData.calculation_data;
            if (inputs) {
              setRegion(inputs.region || 'svealand');
              setAreal(inputs.areal || 50);
              setVirkesvolym(inputs.virkesvolym || 7500);
              setJaktvardeEnabled(inputs.jaktvardeEnabled || false);
              setJaktvarde(inputs.jaktvarde || 500);
            }
            if (savedResult) {
              setResult(savedResult);
            }
          }
          
          setCurrentCaseId(openCaseId);
          setIsPremiumUnlocked(caseData.premium_unlocked);
          
          // Clear only open_case param, keep calc param to maintain calculator selection
          searchParams.delete('open_case');
          // Keep calc=fastighetsvarde to prevent calculator from closing
          if (!searchParams.get('calc')) {
            searchParams.set('calc', 'fastighetsvarde');
          }
          setSearchParams(searchParams, { replace: true });
        })
        .catch(err => {
          console.error('Failed to open case:', err);
          setPaymentError(language === 'sv' ? 'Kunde inte ladda ärendet' : 'Could not load case');
        })
        .finally(() => {
          setIsLoadingCase(false);
        });
    }
  }, [searchParams, isLoggedIn, isLoadingCase, setSearchParams, language]);

  const calculateValue = () => {
    const factors = regionalFactors[region];
    const baseValue = virkesvolym * factors.pricePerM3sk;
    const landValue = areal * factors.landValueFactor * factors.pricePerM3sk * 100;
    const huntingTotal = jaktvardeEnabled ? jaktvarde * areal : 0;
    
    const totalBase = baseValue + landValue + huntingTotal;
    const lowEstimate = totalBase * 0.85;
    const highEstimate = totalBase * 1.15;
    
    setResult({
      lowEstimate: Math.round(lowEstimate),
      highEstimate: Math.round(highEstimate),
      midEstimate: Math.round(totalBase),
      pricePerHa: Math.round(totalBase / areal),
      pricePerM3sk: Math.round(totalBase / virkesvolym)
    });
    setIsSaved(false);
    setIsPremiumUnlocked(false);
    setPaymentError(null);
    setCurrentCaseId(null);
  };

  const handleSave = async () => {
    setIsSaved(true);
  };

  const handleCompare = () => {
    setResult(null);
    setIsPremiumUnlocked(false);
    setCurrentCaseId(null);
  };

  // Real Stripe checkout
  const handleActivatePremium = async () => {
    // Wartemodus: kaufpfad im öffentlichen UI deaktiviert.
    // Short-circuit before any auth redirect or network call.
    if (!SALES_ENABLED) {
      return;
    }

    if (!isLoggedIn) {
      // IMPORTANT: encode the target URL so the nested `?calc=` query string
      // survives as the redirect param (was being silently stripped before).
      const target = encodeURIComponent('/verktyg?calc=fastighetsvarde');
      window.location.href = `/login?redirect=${target}&intent=purchase`;
      return;
    }
    
    setIsProcessingCheckout(true);
    setPaymentError(null);

    // Immediate visible feedback while we create the Stripe session.
    // The inline spinner alone is too subtle on slow networks.
    const toastId = toast.loading(
      language === 'de' ? 'Sichere Zahlung wird vorbereitet…'
      : language === 'en' ? 'Preparing secure payment…'
      : 'Förbereder säker betalning…'
    );
    
    try {
      const calculationData = {
        inputs: { region, areal, virkesvolym, jaktvardeEnabled, jaktvarde },
        result: result
      };
      
      const checkout = await createBeslutslageCheckout('fastighetsvarde', calculationData);
      
      toast.success(
        language === 'de' ? 'Weiterleitung zu Stripe…'
        : language === 'en' ? 'Redirecting to Stripe…'
        : 'Skickar dig till Stripe…',
        { id: toastId }
      );
      
      // Redirect to Stripe Checkout
      window.location.href = checkout.checkout_url;
    } catch (error) {
      console.error('Checkout error:', error);
      setPaymentError(error.message);
      setIsProcessingCheckout(false);
      toast.error(
        (language === 'de' ? 'Zahlung konnte nicht gestartet werden: '
        : language === 'en' ? 'Could not start payment: '
        : 'Kunde inte starta betalningen: ') + error.message,
        { id: toastId }
      );
    }
  };

  // Dev mode unlock (testing only)
  const handleDevUnlock = () => {
    setIsPremiumUnlocked(true);
  };

  const handleDownloadPDF = () => {
    if (result && inputs) {
      downloadBeslutsbrev(result, inputs, language);
    }
  };

  const handlePrint = () => {
    printBeslutsbrev();
  };

  const inputs = {
    region,
    areal,
    virkesvolym,
    jaktvardeEnabled,
    jaktvarde
  };

  // Show payment checking state
  if (isCheckingPayment) {
    return (
      <Card className="w-full">
        <CardContent className="py-12 text-center">
          <Loader2 className="h-12 w-12 animate-spin mx-auto text-forest-600 mb-4" />
          <p className="text-lg font-medium text-forest-800">{c.checkingPayment}</p>
          <p className="text-sm text-muted-foreground mt-2">
            {language === 'sv' ? 'Vänta medan vi bekräftar din betalning...' : 'Please wait...'}
          </p>
        </CardContent>
      </Card>
    );
  }

  // Show loading case state
  if (isLoadingCase) {
    return (
      <Card className="w-full">
        <CardContent className="py-12 text-center">
          <Loader2 className="h-12 w-12 animate-spin mx-auto text-forest-600 mb-4" />
          <p className="text-lg font-medium text-forest-800">
            {c.loadingCase || 'Laddar ärende...'}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="fastighetsvarde-calculator">
      {/* Fullscreen blocking overlay while creating Stripe session
          (toasts disappear on window.location.href redirect — this stays put) */}
      {isProcessingCheckout && (
        <CheckoutLoadingOverlay language={language} />
      )}

      {/* Payment Success Banner */}
      {showPaymentSuccess && (
        <div className="bg-green-50 border-2 border-green-400 rounded-lg p-5 relative shadow-sm" data-testid="payment-success-banner">
          <button 
            onClick={() => setShowPaymentSuccess(false)}
            className="absolute top-2 right-2 text-green-600 hover:text-green-800"
            aria-label="Stäng"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="flex items-start gap-3">
            <CheckCircle className="h-7 w-7 text-green-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-green-800 text-lg">{c.paymentSuccessBanner}</h3>
              <p className="text-sm text-green-700 mt-1">{c.paymentSuccessDesc}</p>
              <p className="text-sm text-green-700 mt-2">
                Om något inte fungerar, kontakta oss på{' '}
                <a
                  href="mailto:info@hodema.se"
                  className="font-medium underline hover:text-green-800"
                  data-testid="payment-success-contact-link"
                >
                  info@hodema.se
                </a>
                .
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Input Form Card */}
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-forest-700">
            <Home className="h-5 w-5" />
            {c.title}
          </CardTitle>
          <CardDescription>{c.description}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{c.region}</Label>
              <Select value={region} onValueChange={setRegion}>
                <SelectTrigger data-testid="region-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="norrland">{c.regions.norrland}</SelectItem>
                  <SelectItem value="svealand">{c.regions.svealand}</SelectItem>
                  <SelectItem value="gotaland">{c.regions.gotaland}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>{c.areal}</Label>
              <Input
                type="number"
                value={areal}
                onChange={(e) => setAreal(Number(e.target.value))}
                min={1}
                data-testid="areal-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>{c.virkesvolym}</Label>
              <Input
                type="number"
                value={virkesvolym}
                onChange={(e) => setVirkesvolym(Number(e.target.value))}
                min={1}
                data-testid="volym-input"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="jaktvarde"
                  checked={jaktvardeEnabled}
                  onChange={(e) => setJaktvardeEnabled(e.target.checked)}
                  className="rounded"
                />
                <Label htmlFor="jaktvarde">{c.includeHunting}</Label>
              </div>
              {jaktvardeEnabled && (
                <Input
                  type="number"
                  value={jaktvarde}
                  onChange={(e) => setJaktvarde(Number(e.target.value))}
                  placeholder={c.huntingValue}
                  data-testid="jakt-input"
                />
              )}
            </div>
          </div>

          <p className="text-xs text-muted-foreground">{c.defaults}</p>

          <Button 
            onClick={calculateValue} 
            className="w-full bg-forest-700 hover:bg-forest-800"
            data-testid="calculate-btn"
          >
            <Calculator className="mr-2 h-4 w-4" />
            {c.calculate}
          </Button>
        </CardContent>
      </Card>

      {/* BESLUTSLÄGE RESULTS */}
      {result && (
        <div className="space-y-4" data-testid="beslutslage-results">
          
          {/* FREE BLOCKS (1-5) */}
          <ResultatBlock result={result} inputs={inputs} language={language} />
          <InterpretationBlock result={result} inputs={inputs} language={language} />
          <UncertaintyBlock result={result} inputs={inputs} language={language} />
          <MissingBlock inputs={inputs} language={language} />
          <DirectionBlock result={result} inputs={inputs} language={language} />
          
          {/* PREMIUM SECTION */}
          {!isPremiumUnlocked ? (
            <>
              {/* Premium Teaser */}
              <BeslutslageTeaser language={language} isComingSoon={false} calculatorType="fastighetsvarde" isLoggedIn={isLoggedIn} />
              
              {/* Payment Cancelled Notice */}
              {paymentCancelled && (
                <Card className="border-amber-200 bg-amber-50" data-testid="payment-cancelled-card">
                  <CardContent className="py-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-amber-800">
                          {c.paymentCancelled || 'Betalningen avbröts'}
                        </p>
                        <p className="text-sm text-amber-700 mt-1">
                          {c.paymentCancelledDesc || 'Du kan fortsätta senare eller försöka igen'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Payment Error */}
              {paymentError && (
                <Card className="border-red-200 bg-red-50" data-testid="payment-error-card">
                  <CardContent className="py-3 space-y-2">
                    <div className="flex items-start gap-2 text-red-700">
                      <XCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      <span className="text-sm">{paymentError}</span>
                    </div>
                    <p className="text-xs text-red-600 pl-6">
                      Försök igen eller kontakta oss på{' '}
                      <a href="mailto:info@hodema.se" className="underline font-medium hover:text-red-800">
                        info@hodema.se
                      </a>
                      .
                    </p>
                  </CardContent>
                </Card>
              )}
              
              {/* Real Payment Button — hidden in maintenance mode */}
              {SALES_ENABLED && (
                <Card className="border-forest-300 bg-gradient-to-br from-forest-50 to-white" data-testid="payment-card">
                  <CardContent className="py-4">
                    {isLoggedIn ? (
                      <Button 
                        onClick={handleActivatePremium}
                        disabled={isProcessingCheckout}
                        className="w-full bg-forest-700 hover:bg-forest-800 text-white h-12 text-base"
                        data-testid="activate-premium-btn"
                      >
                        {isProcessingCheckout ? (
                          <>
                            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                            {c.processing}
                          </>
                        ) : paymentCancelled ? (
                          <>
                            <CreditCard className="mr-2 h-5 w-5" />
                            {c.tryAgain || 'Försök igen'} – 149 kr
                          </>
                        ) : (
                          <>
                            <CreditCard className="mr-2 h-5 w-5" />
                            {c.activatePremium}
                          </>
                        )}
                      </Button>
                    ) : (
                      <Button 
                        onClick={() => {
                          const target = encodeURIComponent('/verktyg?calc=fastighetsvarde');
                          window.location.href = `/login?redirect=${target}&intent=purchase`;
                        }}
                        className="w-full bg-gray-600 hover:bg-gray-700 text-white"
                        data-testid="login-to-buy-btn"
                      >
                        <Lock className="mr-2 h-4 w-4" />
                        {c.loginRequired}
                      </Button>
                    )}
                    
                    <p className="text-xs text-center text-muted-foreground mt-2">
                      {c.stripeSecure || 'Säker betalning via Stripe · Engångskostnad per ärende'}
                    </p>
                  </CardContent>
                </Card>
              )}
              
              {/* Dev Mode Bypass (only shown in dev) */}
              {devMode && (
                <Card className="border-dashed border-2 border-amber-300 bg-amber-50/50" data-testid="dev-unlock-card">
                  <CardContent className="py-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-amber-700">
                        <Lock className="h-4 w-4" />
                        <span className="text-sm font-medium">{c.devMode}</span>
                      </div>
                      <Button 
                        onClick={handleDevUnlock}
                        variant="outline"
                        size="sm"
                        className="border-amber-400 text-amber-700 hover:bg-amber-100"
                        data-testid="dev-unlock-btn"
                      >
                        <Unlock className="mr-2 h-4 w-4" />
                        {c.devUnlock}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <>
              {/* Premium Unlocked Header with PDF Download */}
              <Card className="border-green-300 bg-green-50" data-testid="premium-unlocked-card">
                <CardContent className="py-4">
                  <div className="flex flex-col items-center gap-3 text-green-700">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5" />
                      <span className="font-medium">{c.premiumActive}</span>
                    </div>
                    <p className="text-sm text-green-600 text-center">
                      {c.premiumActiveDesc || 'Ditt beslutsbrev är tillgängligt'}
                    </p>
                    <Button 
                      onClick={handleDownloadPDF}
                      className="bg-green-600 hover:bg-green-700 text-white mt-1"
                      data-testid="quick-pdf-download-btn"
                    >
                      <Download className="mr-2 h-4 w-4" />
                      {language === 'sv' ? 'Ladda ner PDF nu' : 'Download PDF now'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
              
              {/* Premium Content */}
              <BeslutslagePremium 
                language={language}
                result={result}
                inputs={inputs}
                onDownloadPDF={handleDownloadPDF}
                onPrint={handlePrint}
              />
            </>
          )}
          
          {/* SAVE BLOCK (Registered) */}
          <SaveBlock 
            language={language}
            isLoggedIn={isLoggedIn}
            onSave={handleSave}
            onCompare={handleCompare}
            isSaved={isSaved}
          />
        </div>
      )}
    </div>
  );
};

export default FastighetsvardeKalkylator;
